import { LocaleEnum, type Locale } from "@repo/i18n";
import {
  formatDistanceToNow,
  type Locale as DateFnsLocale,
} from "date-fns";
import { cs, enGB } from "date-fns/locale";
import { useCallback } from "react";
import i18n from "./i18next";
import { useLocale } from "./useLocale";

/** BCP 47 tags for `Intl`; `en` is British English (day first, 24-hour clock). */
export const intlLocale: Record<Locale, string> = {
  [LocaleEnum.EN]: "en-GB",
  [LocaleEnum.CS]: "cs-CZ",
};

/** date-fns locales for `format()` tokens such as `P`/`p` and for the calendar. */
export const dateFnsLocale: Record<Locale, DateFnsLocale> = {
  [LocaleEnum.EN]: enGB,
  [LocaleEnum.CS]: cs,
};

const current = (): Locale => (i18n.resolvedLanguage ?? LocaleEnum.EN) as Locale;

/** Named shapes, so screens agree on what "a date" looks like in each language. */
export type DatePreset = "date" | "dateTime" | "dateTimeSeconds" | "time" | "monthDay";

const dateTime: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

const presets: Record<DatePreset, Intl.DateTimeFormatOptions> = {
  date: { day: "numeric", month: "short", year: "numeric" },
  dateTime,
  dateTimeSeconds: { ...dateTime, second: "2-digit" },
  time: { hour: "2-digit", minute: "2-digit", hourCycle: "h23" },
  monthDay: { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" },
};

type DateInput = Date | string | number;

/**
 * `9 Oct 2026, 14:05` / `9. 10. 2026 14:05`. The plain functions read the
 * active language at call time, which is right inside a render that already
 * re-runs on language change and in helpers; the hooks below subscribe.
 */
export const formatDate = (
  value: DateInput,
  preset: DatePreset = "dateTime",
  locale: Locale = current(),
): string =>
  new Intl.DateTimeFormat(intlLocale[locale], presets[preset]).format(new Date(value));

/** `3 minutes ago` / `před 3 minutami`. */
export const formatRelative = (value: DateInput, locale: Locale = current()): string =>
  formatDistanceToNow(new Date(value), { addSuffix: true, locale: dateFnsLocale[locale] });

/** `1,234.5` / `1 234,5`. */
export const formatNumber = (
  value: number,
  options?: Intl.NumberFormatOptions,
  locale: Locale = current(),
): string => new Intl.NumberFormat(intlLocale[locale], options).format(value);

export const useFormatDate = () => {
  const locale = useLocale();
  return useCallback(
    (value: DateInput, preset: DatePreset = "dateTime") => formatDate(value, preset, locale),
    [locale],
  );
};

export const useFormatRelative = () => {
  const locale = useLocale();
  return useCallback((value: DateInput) => formatRelative(value, locale), [locale]);
};

export const useFormatNumber = () => {
  const locale = useLocale();
  return useCallback(
    (value: number, options?: Intl.NumberFormatOptions) => formatNumber(value, options, locale),
    [locale],
  );
};

/** The date-fns locale for `format(date, "P", { locale })` and `<Calendar locale>`. */
export const useDateFnsLocale = (): DateFnsLocale => dateFnsLocale[useLocale()];
